/**
 * TestCaseHistoryTab — change history / audit trail for a test case.
 *
 * - Fetches GET /api/test-cases/:id/history
 * - Groups entries by (changedByName + calendar day) for a session-based timeline
 * - Renders human-readable diffs: "Severity changed from MEDIUM to HIGH"
 * - Capped at last 50 entries (server-enforced)
 */

import { useState, useEffect, useCallback } from 'react';
import { Clock, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config/api';

const API_URL = API_BASE_URL;

interface IFieldChange {
    field:    string;
    oldValue: unknown;
    newValue: unknown;
}

interface IHistoryEntry {
    _id:           string;
    changedBy:     string;
    changedByName: string;
    changedAt:     string;
    diff:          IFieldChange[];
}

interface IHistorySession {
    key:          string;   // "<changedByName>|<YYYY-MM-DD>"
    authorName:   string;
    date:         string;   // e.g. "Mar 3, 2026"
    entries:      IHistoryEntry[];
}

interface TestCaseHistoryTabProps {
    testCaseId: string | null;
}

// ── Human-readable diff formatting ─────────────────────────────────────────

const FIELD_LABELS: Record<string, string> = {
    title:         'Title',
    description:   'Description',
    preconditions: 'Preconditions',
    suite:         'Suite',
    type:          'Type',
    severity:      'Severity',
    caseType:      'Case type',
    tags:          'Tags',
    steps:         'Steps',
    folderId:      'Folder',
    folderPath:    'Folder path',
};

function formatValue(field: string, value: unknown): string {
    if (value === null || value === undefined || value === '') return '(empty)';
    if (field === 'tags' && Array.isArray(value)) {
        return value.length === 0 ? '(none)' : value.join(', ');
    }
    if (field === 'steps' && Array.isArray(value)) {
        return `${(value as unknown[]).length} step${(value as unknown[]).length !== 1 ? 's' : ''}`;
    }
    return String(value);
}

function formatDiff(change: IFieldChange): string {
    const label = FIELD_LABELS[change.field] ?? change.field;
    const oldV  = formatValue(change.field, change.oldValue);
    const newV  = formatValue(change.field, change.newValue);

    if (change.field === 'steps') {
        return `${label} updated (${newV})`;
    }
    // For long text fields truncate display value
    const truncate = (s: string, max = 40) => s.length > max ? `${s.slice(0, max)}…` : s;
    return `${label} changed from "${truncate(oldV)}" → "${truncate(newV)}"`;
}

// ── Date formatting ─────────────────────────────────────────────────────────

function toSessionKey(entry: IHistoryEntry): string {
    const day = new Date(entry.changedAt).toISOString().slice(0, 10); // YYYY-MM-DD
    return `${entry.changedByName}|${day}`;
}

function formatDay(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
}

function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit',
    });
}

// ── Group entries into sessions ─────────────────────────────────────────────

function groupIntoSessions(entries: IHistoryEntry[]): IHistorySession[] {
    const map = new Map<string, IHistorySession>();
    for (const entry of entries) {
        const key = toSessionKey(entry);
        if (!map.has(key)) {
            map.set(key, {
                key,
                authorName: entry.changedByName,
                date: formatDay(entry.changedAt),
                entries: [],
            });
        }
        map.get(key)!.entries.push(entry);
    }
    return Array.from(map.values());
}

// ── Component ─────────────────────────────────────────────────────────────

export function TestCaseHistoryTab({ testCaseId }: TestCaseHistoryTabProps) {
    const { token } = useAuth();

    const [sessions,  setSessions]  = useState<IHistorySession[]>([]);
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState<string | null>(null);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const fetchHistory = useCallback(async () => {
        if (!testCaseId || !token) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/test-cases/${testCaseId}/history`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (data.success && Array.isArray(data.data?.history)) {
                setSessions(groupIntoSessions(data.data.history as IHistoryEntry[]));
            }
        } catch {
            setError('Failed to load history.');
        } finally {
            setLoading(false);
        }
    }, [testCaseId, token]);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    function toggleCollapse(key: string) {
        setCollapsed((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    }

    if (!testCaseId) {
        return (
            <div className="flex flex-col items-center justify-center flex-1 gap-2 text-slate-400 p-4">
                <Clock size={22} className="opacity-40" />
                <p className="text-xs text-center">Save this test case first to see history.</p>
            </div>
        );
    }

    return (
        <div data-testid="test-case-history-tab" className="flex flex-col flex-1 overflow-y-auto p-3 gap-3">
            {loading && (
                <div className="flex justify-center py-6">
                    <Loader2 size={16} className="animate-spin text-slate-400" />
                </div>
            )}
            {error && (
                <p className="text-xs text-rose-500 text-center">{error}</p>
            )}
            {!loading && sessions.length === 0 && !error && (
                <div className="flex flex-col items-center justify-center py-8 gap-2 text-slate-400">
                    <Clock size={20} className="opacity-40" />
                    <p className="text-xs">No history yet. Changes will appear here after the first save.</p>
                </div>
            )}

            {sessions.map((session) => {
                const isCollapsed = collapsed.has(session.key);
                return (
                    <div key={session.key} className="border border-slate-100 dark:border-slate-800 rounded-xl overflow-hidden">
                        {/* Session header */}
                        <button
                            data-testid={`history-session-toggle-${session.key}`}
                            aria-expanded={!isCollapsed}
                            onClick={() => toggleCollapse(session.key)}
                            className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors text-left"
                        >
                            {/* Avatar */}
                            <div className="w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                                {session.authorName.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 flex-1 truncate">
                                {session.authorName}
                            </span>
                            <span className="text-[10px] text-slate-400 flex-shrink-0">{session.date}</span>
                            {isCollapsed
                                ? <ChevronDown size={12} className="text-slate-400 flex-shrink-0" />
                                : <ChevronUp   size={12} className="text-slate-400 flex-shrink-0" />
                            }
                        </button>

                        {/* Session entries */}
                        {!isCollapsed && (
                            <div className="divide-y divide-slate-50 dark:divide-slate-800">
                                {session.entries.map((entry) => (
                                    <div key={entry._id} className="px-3 py-2">
                                        <p className="text-[10px] text-slate-400 mb-1.5">
                                            {formatTime(entry.changedAt)}
                                        </p>
                                        {entry.diff.length === 0 ? (
                                            <p className="text-xs text-slate-500 italic">No tracked changes.</p>
                                        ) : (
                                            <ul className="space-y-1">
                                                {entry.diff.map((change, idx) => (
                                                    <li key={idx} className="flex items-start gap-1.5">
                                                        <span className="mt-0.5 w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
                                                        <span className="text-xs text-slate-600 dark:text-slate-300">
                                                            {formatDiff(change)}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {sessions.length > 0 && (
                <p className="text-[10px] text-slate-400 text-center pb-2">
                    Showing last 50 changes · Oldest changes may be archived
                </p>
            )}
        </div>
    );
}
